import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useEffect, useRef, useState } from "react";
import { CustomInput } from "global/components";
import {
  focusFirstInvalidField,
  InvalidFieldTarget,
} from "utils/accessibility";

import { invalidFieldFocusSx } from "./EnterpriseSSO.styles";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import CustomSwitch from "global/components/CustomSwitch/CustomSwitch";
import CustomAutoCompleteNew from "global/components/CustomAutoCompleteNew/CustomAutoCompleteNew";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import urls from "global/constants/UrlConstants";
import { fetchRoles } from "../../SettingsService";
import {
  activateSsoConnector,
  downloadSamlCertificate,
  reserveSsoConnector,
  searchSsoFallbackUserCandidates,
  updateSsoConnector,
} from "./SsoConfigService";
import {
  OidcConnectionDetailInput,
  SamlConnectionDetailInput,
  SsoConnection,
  SsoConnectionInput,
  SsoFallbackUserCandidate,
  SsoProtocol,
  SsoReserveInput,
  SsoRole,
} from "./SsoConfigTypes";
import {
  hasErrors,
  MAX_SLUG_LENGTH,
  normalizeSlug,
  OidcValidationErrors,
  SamlValidationErrors,
  validateEmailDomain,
  validateOidcConfig,
  validateSamlConfig,
  validateSlug,
} from "./EnterpriseSSOConstant";

const SSO_FIELD_ID = "sso_field_";

interface CustomProps {
  isVisible: boolean;
  handleDialogClose: Function;
  onSuccess?: Function;
  editConfig?: SsoConnection | null;
  defaultSlug?: string;
  /** Full connector list for this account — used for the per-protocol create cap
   *  and the activate-conflict check, both of which are plain client-side reads
   *  against data the parent already has fetched. */
  existingConfigs: SsoConnection[];
}

const ACCENT = "#211446";

type Mode = "metadata" | "manual";
type OidcMode = "issuer" | "manual";

const STEPS = ["Name & domains", "Configure your IdP", "Submit IdP details"];

const PROTOCOL_LABEL: Record<SsoProtocol, string> = {
  SAML: "SAML 2.0",
  OIDC: "OIDC / OAuth 2.0",
};

const CLIENT_AUTH_METHODS: {
  value: "client_secret_basic" | "client_secret_post";
  label: string;
}[] = [
  { value: "client_secret_basic", label: "Client Secret Basic (HTTP Basic header)" },
  { value: "client_secret_post", label: "Client Secret Post (form body)" },
];

const PROMPT_OPTIONS = ["none", "login", "consent", "select_account"];

const parseAcrValues = (raw: string): string[] =>
  raw.trim().length ? raw.trim().split(/\s+/) : [];

const CopyableUrl = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    // `writeText` rejects on a denied permission or an insecure context, so the
    // "Copied!" state has to wait for the promise — otherwise the admin pastes
    // an empty clipboard into their IdP believing the copy worked.
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => openErrorNotification("Couldn't copy to clipboard"));
  };
  return (
    <Box>
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#1D4ED8", mb: 0.25 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography
          sx={{
            fontFamily: "monospace",
            fontSize: "11px",
            wordBreak: "break-all",
            flex: 1,
            bgcolor: "#DBEAFE",
            border: "1px solid #BFDBFE",
            borderRadius: "6px",
            px: 1,
            py: 0.5,
            color: "#1E3A5F",
          }}
        >
          {value}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy"}>
          <IconButton
            size="small"
            onClick={handleCopy}
            aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
            sx={{ flexShrink: 0 }}
          >
            {copied ? (
              <DoneAllIcon sx={{ fontSize: 15, color: "#047857" }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 15, color: "#1D4ED8" }} />
            )}
          </IconButton>
        </Tooltip>
        <Box
          component="span"
          role="status"
          aria-live="polite"
          sx={{
            position: "absolute",
            width: "1px",
            height: "1px",
            p: 0,
            m: "-1px",
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {copied ? `${label} copied` : ""}
        </Box>
      </Box>
    </Box>
  );
};

const AddSsoConnectorModal = (props: CustomProps) => {
  const isEditMode = !!props.editConfig;
  const edit = props.editConfig;
  const editFallbackUserId = edit?.pwdFallbackUserId;

  const [config, setConfig] = useState<SsoConnection | null>(edit ?? null);

  const [step, setStep] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [certDownloading, setCertDownloading] = useState<"" | "crt" | "pem">("");

  const [protocol, setProtocol] = useState<SsoProtocol>(() => {
    if (edit?.protocol) return edit.protocol;
    const samlTaken = props.existingConfigs.some(
      (c) => (c.protocol ?? "SAML") === "SAML",
    );
    return samlTaken ? "OIDC" : "SAML";
  });

  const [slug, setSlug] = useState<string>(edit?.slug ?? "");
  const [slugError, setSlugError] = useState<string>("");

  const [emailDomains, setEmailDomains] = useState<string[]>(
    edit?.emailDomains ?? [],
  );
  const [domainDraft, setDomainDraft] = useState<string>("");
  const [domainError, setDomainError] = useState<string>("");

  // ---- SAML fields ----
  const [mode, setMode] = useState<Mode>(
    edit && !edit.saml?.metadataUrl && edit.saml?.idpEntityId
      ? "manual"
      : "metadata",
  );
  const [metadataUrl, setMetadataUrl] = useState<string>(
    edit?.saml?.metadataUrl ?? "",
  );
  const [idpEntityId, setIdpEntityId] = useState<string>(
    edit?.saml?.idpEntityId ?? "",
  );
  const [idpSsoUrl, setIdpSsoUrl] = useState<string>(
    edit?.saml?.idpSsoUrl ?? "",
  );
  const [idpVerificationCertificate, setIdpVerificationCertificate] =
    useState<string>(edit?.saml?.idpVerificationCertificate ?? "");
  const [idpSloUrl, setIdpSloUrl] = useState<string>(
    edit?.saml?.idpSloUrl ?? "",
  );
  const [spEntityId, setSpEntityId] = useState<string>(
    edit?.saml?.spEntityId ?? "",
  );
  const [spCertificate, setSpCertificate] = useState<string>(
    edit?.saml?.spCertificate ?? "",
  );
  const [spPrivateKeyRef, setSpPrivateKeyRef] = useState<string>("");
  const [spPrivateKey, setSpPrivateKey] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<SamlValidationErrors>({});

  // ---- OIDC fields ----
  const [oidcMode, setOidcMode] = useState<OidcMode>(
    edit && !edit.oidc?.issuer && edit.oidc?.authorizationUri
      ? "manual"
      : "issuer",
  );
  const [issuer, setIssuer] = useState<string>(edit?.oidc?.issuer ?? "");
  const [authorizationUri, setAuthorizationUri] = useState<string>(
    edit?.oidc?.authorizationUri ?? "",
  );
  const [tokenUri, setTokenUri] = useState<string>(edit?.oidc?.tokenUri ?? "");
  const [userInfoUri, setUserInfoUri] = useState<string>(
    edit?.oidc?.userInfoUri ?? "",
  );
  const [jwkSetUri, setJwkSetUri] = useState<string>(
    edit?.oidc?.jwkSetUri ?? "",
  );
  const [endSessionUri, setEndSessionUri] = useState<string>(
    edit?.oidc?.endSessionUri ?? "",
  );
  const [clientId, setClientId] = useState<string>(edit?.oidc?.clientId ?? "");
  const [clientSecret, setClientSecret] = useState<string>("");
  const [clientAuthenticationMethod, setClientAuthenticationMethod] = useState<
    "client_secret_basic" | "client_secret_post" | ""
  >(edit?.oidc?.clientAuthenticationMethod ?? "client_secret_basic");
  const [usePkce, setUsePkce] = useState<boolean>(edit?.oidc?.usePkce ?? true);
  const [scopes, setScopes] = useState<string[]>(
    edit?.oidc?.scopes?.length ? edit.oidc.scopes : ["openid", "email", "profile"],
  );
  const [scopeDraft, setScopeDraft] = useState<string>("");
  const [scopeError, setScopeError] = useState<string>("");
  const [acrValuesInput, setAcrValuesInput] = useState<string>(
    (edit?.oidc?.acrValues ?? []).join(" "),
  );
  const [prompt, setPrompt] = useState<string>(edit?.oidc?.prompt ?? "");
  const [maxAge, setMaxAge] = useState<number | "">(edit?.oidc?.maxAge ?? "");
  const [oidcFieldErrors, setOidcFieldErrors] = useState<OidcValidationErrors>(
    {},
  );

  // ---- Shared ----
  const [emailClaim, setEmailClaim] = useState<string>(edit?.emailClaim ?? "");

  const [enforceSso, setEnforceSso] = useState<boolean>(
    edit?.enforceSso ?? false,
  );
  const [pwdFallbackUserId, setPwdFallbackUserId] = useState<number | "">(
    edit?.pwdFallbackUserId ?? "",
  );
  const [pwdFallbackUserSelected, setPwdFallbackUserSelected] =
    useState<SsoFallbackUserCandidate | null>(null);
  const [pwdFallbackUserOptions, setPwdFallbackUserOptions] = useState<
    SsoFallbackUserCandidate[]
  >([]);
  const [pwdFallbackUserLoading, setPwdFallbackUserLoading] =
    useState<boolean>(false);
  const pwdFallbackSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [sendLoginHint, setSendLoginHint] = useState<boolean>(
    edit?.sendLoginHint ?? true,
  );
  const [active, setActive] = useState<boolean>(
    edit ? edit.status !== "Inactive" : true,
  );

  const [autoProvisionUsers, setAutoProvisionUsers] = useState<boolean>(
    edit?.autoProvisionUsers ?? false,
  );
  const [defaultRoleId, setDefaultRoleId] = useState<number | "">(
    edit?.defaultRoleId ?? "",
  );
  const [defaultRoleError, setDefaultRoleError] = useState<string>("");

  const [roles, setRoles] = useState<SsoRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState<boolean>(false);

  const [confirmActivation, setConfirmActivation] = useState<{
    sibling: SsoConnection;
    run: () => void;
  } | null>(null);

  // True once the connector has been through a full activation at least once —
  // at that point a client secret is already stored server-side (write-only,
  // never returned), so leaving the field blank on edit means "keep it".
  const oidcHasStoredSecret = isEditMode && config?.status !== "Draft";

  useEffect(() => {
    let mounted = true;
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const data = await fetchRoles();
        if (!mounted) return;
        const list: SsoRole[] = (Array.isArray(data) ? data : []).map(
          (role: any) => ({ id: role.id, name: role.name }),
        );
        setRoles(list);
      } catch (error: any) {
        if (mounted) setRoles([]);
      } finally {
        if (mounted) setRolesLoading(false);
      }
    };
    loadRoles();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadFallbackUserCandidates = async () => {
      setPwdFallbackUserLoading(true);
      setPwdFallbackUserSelected(null);
      try {
        const data = await searchSsoFallbackUserCandidates("");
        if (!mounted) return;
        const list = Array.isArray(data) ? data : [];
        setPwdFallbackUserOptions(list);
        if (editFallbackUserId) {
          const current = list.find((u) => u.id === editFallbackUserId);
          if (current) setPwdFallbackUserSelected(current);
        }
      } catch (error: any) {
        if (mounted) setPwdFallbackUserOptions([]);
      } finally {
        if (mounted) setPwdFallbackUserLoading(false);
      }
    };
    loadFallbackUserCandidates();
    return () => {
      mounted = false;
    };
  }, [editFallbackUserId]);

  useEffect(() => {
    return () => {
      if (pwdFallbackSearchTimeoutRef.current) {
        clearTimeout(pwdFallbackSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pwdFallbackSearchTimeoutRef.current) {
        clearTimeout(pwdFallbackSearchTimeoutRef.current);
      }
    };
  }, []);

  const searchFallbackUsers = (query: string) => {
    if (pwdFallbackSearchTimeoutRef.current) {
      clearTimeout(pwdFallbackSearchTimeoutRef.current);
    }
    pwdFallbackSearchTimeoutRef.current = setTimeout(async () => {
      setPwdFallbackUserLoading(true);
      try {
        const data = await searchSsoFallbackUserCandidates(query);
        setPwdFallbackUserOptions(Array.isArray(data) ? data : []);
      } catch (error: any) {
        setPwdFallbackUserOptions([]);
      } finally {
        setPwdFallbackUserLoading(false);
      }
    }, 400);
  };

  const findActiveSibling = (proto: SsoProtocol): SsoConnection | undefined =>
    props.existingConfigs.find(
      (c) => (c.protocol ?? "SAML") !== proto && c.status === "Active",
    );

  const addDomain = (raw: string) => {
    const value = raw.trim().toLowerCase().replace(/,$/, "");
    if (!value) return;
    const error = validateEmailDomain(value);
    if (error) {
      setDomainError(error);
      return;
    }
    if (emailDomains.includes(value)) {
      setDomainError("Domain already added");
      return;
    }
    setEmailDomains((prev) => [...prev, value]);
    setDomainDraft("");
    setDomainError("");
  };

  const handleDomainKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addDomain(domainDraft);
    }
  };

  const removeDomain = (domain: string) => {
    setEmailDomains((prev) => prev.filter((d) => d !== domain));
  };

  const commitPendingDomain = (): string[] => {
    let domains = emailDomains;
    const draft = domainDraft.trim().toLowerCase().replace(/,$/, "");
    if (draft && !validateEmailDomain(draft) && !domains.includes(draft)) {
      domains = [...domains, draft];
      setEmailDomains(domains);
      setDomainDraft("");
    }
    return domains;
  };

  const addScope = (raw: string) => {
    const value = raw.trim().replace(/,$/, "");
    if (!value) return;
    if (scopes.includes(value)) {
      setScopeError("Scope already added");
      return;
    }
    setScopes((prev) => [...prev, value]);
    setScopeDraft("");
    setScopeError("");
  };

  const handleScopeKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addScope(scopeDraft);
    }
  };

  const removeScope = (scope: string) => {
    if (scope === "openid") return;
    setScopes((prev) => prev.filter((s) => s !== scope));
  };

  const validateReserve = (domains: string[]): boolean => {
    const sErr = validateSlug(slug);
    setSlugError(sErr);

    let dErr = "";
    if (!domains.length) dErr = "Add at least one email domain";
    setDomainError(dErr);

    let roleError = "";
    if (autoProvisionUsers && defaultRoleId === "") {
      roleError = "Select a default role for new SSO users";
    }
    setDefaultRoleError(roleError);

    if (sErr || dErr || roleError) {
      focusFirstInvalidField([
        sErr && `${SSO_FIELD_ID}slug`,
        dErr && `${SSO_FIELD_ID}emailDomain`,
        roleError && `${SSO_FIELD_ID}defaultRoleId`,
      ]);
    }

    return !sErr && !dErr && !roleError;
  };

  const validateIdp = (domains: string[]): boolean => {
    let ok: boolean;
    const fieldTargets: InvalidFieldTarget[] = [];
    if (protocol === "SAML") {
      const errors = validateSamlConfig({
        mode,
        emailDomains: domains,
        metadataUrl,
        idpEntityId,
        idpSsoUrl,
        idpVerificationCertificate,
        idpSloUrl,
      });
      setFieldErrors(errors);
      ok = !hasErrors(errors);
      fieldTargets.push(
        errors.metadataUrl && `${SSO_FIELD_ID}metadataUrl`,
        errors.idpEntityId && `${SSO_FIELD_ID}idpEntityId`,
        errors.idpSsoUrl && `${SSO_FIELD_ID}idpSsoUrl`,
        errors.idpVerificationCertificate &&
          `${SSO_FIELD_ID}idpVerificationCertificate`,
        errors.idpSloUrl && `${SSO_FIELD_ID}idpSloUrl`,
      );
    } else {
      const errors = validateOidcConfig({
        mode: oidcMode,
        emailDomains: domains,
        issuer,
        authorizationUri,
        tokenUri,
        userInfoUri,
        jwkSetUri,
        endSessionUri,
        clientId,
        clientSecret,
        hasStoredClientSecret: oidcHasStoredSecret,
        clientAuthenticationMethod,
        scopes,
      });
      setOidcFieldErrors(errors);
      ok = !hasErrors(errors);
      fieldTargets.push(
        errors.issuer && `${SSO_FIELD_ID}issuer`,
        errors.authorizationUri && `${SSO_FIELD_ID}authorizationUri`,
        errors.tokenUri && `${SSO_FIELD_ID}tokenUri`,
        errors.userInfoUri && `${SSO_FIELD_ID}userInfoUri`,
        errors.jwkSetUri && `${SSO_FIELD_ID}jwkSetUri`,
        errors.endSessionUri && `${SSO_FIELD_ID}endSessionUri`,
        errors.clientId && `${SSO_FIELD_ID}clientId`,
        errors.clientSecret && `${SSO_FIELD_ID}clientSecret`,
        errors.clientAuthenticationMethod &&
          `${SSO_FIELD_ID}clientAuthenticationMethod`,
        errors.scopes && `${SSO_FIELD_ID}scopeDraft`,
      );
    }

    let dErr = "";
    if (!domains.length) dErr = "Add at least one email domain";
    setDomainError(dErr);

    let roleError = "";
    if (autoProvisionUsers && defaultRoleId === "") {
      roleError = "Select a default role for new SSO users";
    }
    setDefaultRoleError(roleError);

    if (!ok || dErr || roleError) {
      focusFirstInvalidField([
        dErr && `${SSO_FIELD_ID}emailDomain`,
        ...fieldTargets,
        roleError && `${SSO_FIELD_ID}defaultRoleId`,
      ]);
    }

    return ok && !dErr && !roleError;
  };

  const buildReserveBody = (domains: string[]): SsoReserveInput => {
    const body: SsoReserveInput = {
      protocol,
      emailDomains: domains,
      enforceSso,
      sendLoginHint,
      autoProvisionUsers,
    };
    if (slug.trim()) body.slug = slug.trim();
    if (pwdFallbackUserId !== "") body.pwdFallbackUserId = pwdFallbackUserId;
    if (autoProvisionUsers && defaultRoleId !== "")
      body.defaultRoleId = defaultRoleId;
    return body;
  };

  const buildUpdateBody = (domains: string[]): SsoConnectionInput => {
    const body: SsoConnectionInput = {
      emailDomains: domains,
      enforceSso,
      sendLoginHint,
      autoProvisionUsers,
    };

    if (pwdFallbackUserId !== "") body.pwdFallbackUserId = pwdFallbackUserId;
    if (autoProvisionUsers && defaultRoleId !== "")
      body.defaultRoleId = defaultRoleId;
    if (emailClaim.trim()) body.emailClaim = emailClaim.trim();

    if (protocol === "SAML") {
      const saml: SamlConnectionDetailInput = {};
      if (mode === "metadata") {
        saml.metadataUrl = metadataUrl.trim();
      } else {
        saml.idpEntityId = idpEntityId.trim();
        saml.idpSsoUrl = idpSsoUrl.trim();
        saml.idpVerificationCertificate = idpVerificationCertificate.trim();
        if (idpSloUrl.trim()) saml.idpSloUrl = idpSloUrl.trim();
      }
      if (spEntityId.trim()) saml.spEntityId = spEntityId.trim();
      if (spCertificate.trim()) saml.spCertificate = spCertificate.trim();
      if (spPrivateKeyRef.trim()) saml.spPrivateKeyRef = spPrivateKeyRef.trim();
      if (spPrivateKey.trim()) saml.spPrivateKey = spPrivateKey.trim();
      body.saml = saml;
    } else {
      const oidc: OidcConnectionDetailInput = {
        clientId: clientId.trim(),
        scopes,
        usePkce,
      };
      if (clientAuthenticationMethod)
        oidc.clientAuthenticationMethod = clientAuthenticationMethod;
      if (oidcMode === "issuer") {
        oidc.issuer = issuer.trim();
      } else {
        oidc.authorizationUri = authorizationUri.trim();
        oidc.tokenUri = tokenUri.trim();
        oidc.userInfoUri = userInfoUri.trim();
        oidc.jwkSetUri = jwkSetUri.trim();
      }
      if (endSessionUri.trim()) oidc.endSessionUri = endSessionUri.trim();
      if (clientSecret.trim()) oidc.clientSecret = clientSecret.trim();
      const acrValuesArr = parseAcrValues(acrValuesInput);
      if (acrValuesArr.length) oidc.acrValues = acrValuesArr;
      if (prompt.trim()) oidc.prompt = prompt.trim();
      if (maxAge !== "") oidc.maxAge = maxAge;
      body.oidc = oidc;
    }

    if (isEditMode && config?.status !== "Draft") {
      body.status = active ? "Active" : "Inactive";
    }

    return body;
  };

  const handleApiError = (error: any) => {
    const code = error?.code ?? error?.status ?? error?.statusCode;
    if (code === 400 || code === 409) {
      openErrorNotification(
        error?.message || "Please check the details and try again.",
      );
      return;
    }
    handleErrorMessage(error);
  };

  const reserveHandler = async () => {
    const domains = commitPendingDomain();
    if (!validateReserve(domains)) return;
    try {
      setIsLoading(true);
      const created = await reserveSsoConnector(buildReserveBody(domains));
      if (!created?.registrationId?.trim()) {
        openErrorNotification(
          "The connector was reserved but no registration ID was returned. Please try again.",
        );
        return;
      }
      openSuccessNotification(
        protocol === "SAML"
          ? "Connector reserved — copy the SP URLs into your IdP."
          : "Connector reserved — copy the redirect URI into your IdP.",
      );
      props.onSuccess?.();
      setConfig(created);
      setStep(1);
    } catch (error: any) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const runActivate = async (domains: string[]) => {
    if (!config) return;
    try {
      setIsLoading(true);
      await updateSsoConnector(config.registrationId, buildUpdateBody(domains));
      openSuccessNotification("SSO is now live for this connector.");
      props.onSuccess?.();
      props.handleDialogClose();
    } catch (error: any) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
      setConfirmActivation(null);
    }
  };

  const activateHandler = async () => {
    if (!config) return;
    const domains = commitPendingDomain();
    if (!validateIdp(domains)) return;
    const sibling = findActiveSibling(protocol);
    if (sibling) {
      setConfirmActivation({ sibling, run: () => runActivate(domains) });
      return;
    }
    await runActivate(domains);
  };

  const runSaveEdit = async (domains: string[], activatingNow: boolean) => {
    if (!config) return;
    try {
      setIsLoading(true);
      const body = buildUpdateBody(domains);
      // The dedicated activate endpoint owns the Active transition + sibling
      // deactivation swap for this path; this call only persists the rest of
      // the field edits.
      if (activatingNow) delete body.status;
      await updateSsoConnector(config.registrationId, body);
      if (activatingNow) await activateSsoConnector(config.registrationId);
      openSuccessNotification("SSO configuration updated successfully");
      props.onSuccess?.();
      props.handleDialogClose();
    } catch (error: any) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
      setConfirmActivation(null);
    }
  };

  const saveEditHandler = async () => {
    if (!config) return;
    const domains = commitPendingDomain();
    if (!validateIdp(domains)) return;

    const activatingNow = active && config.status !== "Active";
    if (activatingNow) {
      const sibling = findActiveSibling(protocol);
      if (sibling) {
        setConfirmActivation({
          sibling,
          run: () => runSaveEdit(domains, activatingNow),
        });
        return;
      }
    }
    await runSaveEdit(domains, activatingNow);
  };

  const handleDownloadCertificate = async (format: "crt" | "pem") => {
    if (!config) return;
    try {
      setCertDownloading(format);
      const blob = await downloadSamlCertificate(config.registrationId, format);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${config.slug || config.registrationId}-sp-certificate.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setCertDownloading("");
    }
  };

  const renderProtocolToggle = () => {
    const samlTaken = props.existingConfigs.some(
      (c) => (c.protocol ?? "SAML") === "SAML",
    );
    const oidcTaken = props.existingConfigs.some(
      (c) => (c.protocol ?? "SAML") === "OIDC",
    );
    return (
      <Box>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#1E293B", mb: 1 }}
        >
          Protocol
        </Typography>
        <ToggleButtonGroup
          color="primary"
          exclusive
          size="small"
          value={protocol}
          onChange={(_e, next: SsoProtocol | null) => {
            if (next) {
              setProtocol(next);
              setFieldErrors({});
              setOidcFieldErrors({});
            }
          }}
          fullWidth
        >
          <ToggleButton value="SAML" disabled={samlTaken} sx={{ textTransform: "none" }}>
            SAML 2.0
          </ToggleButton>
          <ToggleButton value="OIDC" disabled={oidcTaken} sx={{ textTransform: "none" }}>
            OIDC / OAuth 2.0
          </ToggleButton>
        </ToggleButtonGroup>
        {(samlTaken || oidcTaken) && (
          <Typography sx={{ fontSize: "11px", color: "#94A3B8", mt: 0.5 }}>
            Only one connector per protocol is supported per account.
          </Typography>
        )}
      </Box>
    );
  };

  const renderDomainsField = () => (
    <Box>
      <CustomInput
        required
        label="Email Domains"
        placeHolder="Type a domain and press Enter, e.g. acme.com"
        name="emailDomain"
        id={`${SSO_FIELD_ID}emailDomain`}
        sx={invalidFieldFocusSx}
        value={domainDraft}
        onChange={(e: any) => {
          const v = e.target.value;
          setDomainError("");
          if (v.endsWith(",")) addDomain(v);
          else setDomainDraft(v);
        }}
        onKeyPress={handleDomainKeyPress}
        error={!!domainError}
        helperText={domainError}
      />
      {emailDomains.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
          {emailDomains.map((domain) => (
            <Chip
              key={domain}
              label={domain}
              onDelete={() => removeDomain(domain)}
              size="small"
              sx={{ backgroundColor: "#EEF0F6", color: ACCENT, fontWeight: 600 }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );

  const renderPolicySwitches = () => (
    <Stack
      spacing={1.5}
      sx={{ border: "1px solid #E2E8F0", borderRadius: "10px", p: 1.5 }}
    >
      <FormControlLabel
        control={
          <CustomSwitch
            checked={enforceSso}
            onChange={(e: any) => setEnforceSso(e.target.checked)}
            name="enforceSso"
          />
        }
        label={
          <Typography sx={{ fontSize: "13px", color: "#475569", ml: 1 }}>
            Enforce SSO (block password login for these email domains)
          </Typography>
        }
        sx={{ ml: 0 }}
      />
      <Box sx={{ opacity: enforceSso ? 1 : 0.5 }}>
        <CustomAutoCompleteNew
          id="sso-pwd-fallback-user"
          label="Password-fallback user (break-glass account)"
          requiredIndicator={false}
          placeHolder="Search users by name or email…"
          disabled={!enforceSso}
          loading={pwdFallbackUserLoading}
          options={pwdFallbackUserOptions}
          value={pwdFallbackUserSelected}
          disableCloseOnSelect={false}
          clearIcon
          getOptionLabel={(option: SsoFallbackUserCandidate | null) =>
            option ? `${option.name} (${option.email})` : ""
          }
          renderOption={(props: any, option: SsoFallbackUserCandidate) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" fontWeight={600}>
                  {option.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "grey" }}>
                  {option.email}
                </Typography>
              </Box>
            </li>
          )}
          onChangeForTextField={(e: React.ChangeEvent<HTMLInputElement>) =>
            searchFallbackUsers(e.target.value)
          }
          onChange={(
            _e: any,
            newValue: SsoFallbackUserCandidate | null,
          ) => {
            setPwdFallbackUserSelected(newValue);
            setPwdFallbackUserId(newValue ? newValue.id : "");
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#94A3B8", mt: 0.5 }}>
          This one user can still sign in with a password even when SSO is
          enforced — defaults to whoever set up SSO. Everyone else must use
          SSO.
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <CustomSwitch
            checked={sendLoginHint}
            onChange={(e: any) => setSendLoginHint(e.target.checked)}
            name="sendLoginHint"
          />
        }
        label={
          <Typography sx={{ fontSize: "13px", color: "#475569", ml: 1 }}>
            Send login hint (pre-fill the user's email at the IdP)
          </Typography>
        }
        sx={{ ml: 0 }}
      />
      {isEditMode && config?.status !== "Draft" && (
        <FormControlLabel
          control={
            <CustomSwitch
              checked={active}
              onChange={(e: any) => setActive(e.target.checked)}
              name="status"
            />
          }
          label={
            <Typography sx={{ fontSize: "13px", color: "#475569", ml: 1 }}>
              {active ? "Active" : "Paused (Inactive)"}
            </Typography>
          }
          sx={{ ml: 0 }}
        />
      )}
    </Stack>
  );

  const renderProvisioning = () => (
    <Stack
      spacing={1.5}
      sx={{ border: "1px solid #E2E8F0", borderRadius: "10px", p: 1.5 }}
    >
      <FormControlLabel
        control={
          <CustomSwitch
            checked={autoProvisionUsers}
            onChange={(e: any) => {
              setAutoProvisionUsers(e.target.checked);
              if (!e.target.checked) setDefaultRoleError("");
            }}
            name="autoProvisionUsers"
          />
        }
        label={
          <Typography sx={{ fontSize: "13px", color: "#475569", ml: 1 }}>
            Automatically onboard new SSO users
          </Typography>
        }
        sx={{ ml: 0 }}
      />
      <Collapse in={autoProvisionUsers}>
        <FormControl
          fullWidth
          size="small"
          error={!!defaultRoleError}
          disabled={rolesLoading}
        >
          <InputLabel id="sso-default-role-label" required>
            Default role for new users
          </InputLabel>
          <Select
            labelId="sso-default-role-label"
            SelectDisplayProps={{
              id: `${SSO_FIELD_ID}defaultRoleId`,
              "aria-invalid": Boolean(defaultRoleError) || undefined,
              "aria-describedby": `${SSO_FIELD_ID}defaultRoleId_helper`,
            }}
            sx={invalidFieldFocusSx}
            name="defaultRoleId"
            label="Default role for new users"
            value={defaultRoleId === "" ? "" : String(defaultRoleId)}
            onChange={(e) => {
              setDefaultRoleId(Number(e.target.value));
              setDefaultRoleError("");
            }}
          >
            {roles.length === 0 && (
              <MenuItem value="" disabled>
                {rolesLoading ? "Loading roles…" : "No roles available"}
              </MenuItem>
            )}
            {roles.map((role) => (
              <MenuItem key={role.id} value={String(role.id)}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText id={`${SSO_FIELD_ID}defaultRoleId_helper`}>
            {defaultRoleError ||
              "First-time SSO users are created with this role."}
          </FormHelperText>
        </FormControl>
      </Collapse>
    </Stack>
  );

  const renderIdpFields = () => (
    <>
      <Box>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#1E293B", mb: 1 }}
        >
          IdP Configuration
        </Typography>
        <ToggleButtonGroup
          color="primary"
          exclusive
          size="small"
          value={mode}
          onChange={(_e, next: Mode | null) => {
            if (next) setMode(next);
            setFieldErrors({});
          }}
          fullWidth
        >
          <ToggleButton value="metadata" sx={{ textTransform: "none" }}>
            Metadata URL
          </ToggleButton>
          <ToggleButton value="manual" sx={{ textTransform: "none" }}>
            Manual entry
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === "metadata" ? (
        <CustomInput
          required
          label="IdP Metadata URL"
          placeHolder="https://login.microsoftonline.com/<tenant>/federationmetadata/2007-06/federationmetadata.xml"
          name="metadataUrl"
          id={`${SSO_FIELD_ID}metadataUrl`}
          sx={invalidFieldFocusSx}
          value={metadataUrl}
          onChange={(e: any) => setMetadataUrl(e.target.value)}
          error={!!fieldErrors.metadataUrl}
          helperText={fieldErrors.metadataUrl}
        />
      ) : (
        <Stack spacing={2}>
          <CustomInput
            required
            label="IdP Entity ID / Issuer"
            placeHolder="https://idp.acme.com/saml/metadata"
            name="idpEntityId"
            id={`${SSO_FIELD_ID}idpEntityId`}
            sx={invalidFieldFocusSx}
            value={idpEntityId}
            onChange={(e: any) => setIdpEntityId(e.target.value)}
            error={!!fieldErrors.idpEntityId}
            helperText={fieldErrors.idpEntityId}
          />
          <CustomInput
            required
            label="IdP SSO URL"
            placeHolder="https://idp.acme.com/saml/sso"
            name="idpSsoUrl"
            id={`${SSO_FIELD_ID}idpSsoUrl`}
            sx={invalidFieldFocusSx}
            value={idpSsoUrl}
            onChange={(e: any) => setIdpSsoUrl(e.target.value)}
            error={!!fieldErrors.idpSsoUrl}
            helperText={fieldErrors.idpSsoUrl}
          />
          <CustomInput
            required
            multiline
            minRows={4}
            maxRows={10}
            label="IdP Signing Certificate (PEM)"
            placeHolder={"-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"}
            name="idpVerificationCertificate"
            id={`${SSO_FIELD_ID}idpVerificationCertificate`}
            sx={invalidFieldFocusSx}
            value={idpVerificationCertificate}
            onChange={(e: any) => setIdpVerificationCertificate(e.target.value)}
            error={!!fieldErrors.idpVerificationCertificate}
            helperText={fieldErrors.idpVerificationCertificate}
          />
          <CustomInput
            label="IdP Single Logout URL (optional)"
            placeHolder="https://idp.acme.com/app/exkxyz/slo/saml"
            name="idpSloUrl"
            id={`${SSO_FIELD_ID}idpSloUrl`}
            sx={invalidFieldFocusSx}
            value={idpSloUrl}
            onChange={(e: any) => setIdpSloUrl(e.target.value)}
            error={!!fieldErrors.idpSloUrl}
            helperText={
              fieldErrors.idpSloUrl ||
              "If your IdP supports SAML Single Logout and you want signing out of this app to also sign the user out of your IdP, paste its Single Logout URL here."
            }
          />
        </Stack>
      )}
    </>
  );

  const renderScopesField = () => (
    <Box>
      <CustomInput
        label="Scopes"
        placeHolder="Type a scope and press Enter, e.g. profile"
        name="scopeDraft"
        id={`${SSO_FIELD_ID}scopeDraft`}
        sx={invalidFieldFocusSx}
        value={scopeDraft}
        onChange={(e: any) => {
          const v = e.target.value;
          setScopeError("");
          if (v.endsWith(",")) addScope(v);
          else setScopeDraft(v);
        }}
        onKeyPress={handleScopeKeyPress}
        error={!!scopeError || !!oidcFieldErrors.scopes}
        helperText={
          scopeError || oidcFieldErrors.scopes || '"openid" is always included.'
        }
      />
      <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
        {scopes.map((scope) => (
          <Chip
            key={scope}
            label={scope}
            onDelete={scope === "openid" ? undefined : () => removeScope(scope)}
            size="small"
            sx={{ backgroundColor: "#EEF0F6", color: ACCENT, fontWeight: 600 }}
          />
        ))}
      </Stack>
    </Box>
  );

  const renderOidcFields = () => (
    <>
      <Box>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#1E293B", mb: 1 }}
        >
          IdP Configuration
        </Typography>
        <ToggleButtonGroup
          color="primary"
          exclusive
          size="small"
          value={oidcMode}
          onChange={(_e, next: OidcMode | null) => {
            if (next) setOidcMode(next);
            setOidcFieldErrors({});
          }}
          fullWidth
        >
          <ToggleButton value="issuer" sx={{ textTransform: "none" }}>
            Issuer URL
          </ToggleButton>
          <ToggleButton value="manual" sx={{ textTransform: "none" }}>
            Manual entry
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {oidcMode === "issuer" ? (
        <CustomInput
          required
          label="Issuer URL"
          placeHolder="https://login.microsoftonline.com/<tenant>/v2.0"
          name="issuer"
          id={`${SSO_FIELD_ID}issuer`}
          sx={invalidFieldFocusSx}
          value={issuer}
          onChange={(e: any) => setIssuer(e.target.value)}
          error={!!oidcFieldErrors.issuer}
          helperText={
            oidcFieldErrors.issuer ||
            "We'll discover the authorization, token, user info and JWK set endpoints from this issuer's OIDC metadata."
          }
        />
      ) : (
        <Stack spacing={2}>
          <CustomInput
            required
            label="Authorization Endpoint"
            placeHolder="https://idp.acme.com/oauth2/v1/authorize"
            name="authorizationUri"
            id={`${SSO_FIELD_ID}authorizationUri`}
            sx={invalidFieldFocusSx}
            value={authorizationUri}
            onChange={(e: any) => setAuthorizationUri(e.target.value)}
            error={!!oidcFieldErrors.authorizationUri}
            helperText={oidcFieldErrors.authorizationUri}
          />
          <CustomInput
            required
            label="Token Endpoint"
            placeHolder="https://idp.acme.com/oauth2/v1/token"
            name="tokenUri"
            id={`${SSO_FIELD_ID}tokenUri`}
            sx={invalidFieldFocusSx}
            value={tokenUri}
            onChange={(e: any) => setTokenUri(e.target.value)}
            error={!!oidcFieldErrors.tokenUri}
            helperText={oidcFieldErrors.tokenUri}
          />
          <CustomInput
            required
            label="User Info Endpoint"
            placeHolder="https://idp.acme.com/oauth2/v1/userinfo"
            name="userInfoUri"
            id={`${SSO_FIELD_ID}userInfoUri`}
            sx={invalidFieldFocusSx}
            value={userInfoUri}
            onChange={(e: any) => setUserInfoUri(e.target.value)}
            error={!!oidcFieldErrors.userInfoUri}
            helperText={oidcFieldErrors.userInfoUri}
          />
          <CustomInput
            required
            label="JWK Set Endpoint"
            placeHolder="https://idp.acme.com/oauth2/v1/keys"
            name="jwkSetUri"
            id={`${SSO_FIELD_ID}jwkSetUri`}
            sx={invalidFieldFocusSx}
            value={jwkSetUri}
            onChange={(e: any) => setJwkSetUri(e.target.value)}
            error={!!oidcFieldErrors.jwkSetUri}
            helperText={oidcFieldErrors.jwkSetUri}
          />
        </Stack>
      )}

      <CustomInput
        label="End Session Endpoint (optional)"
        placeHolder="https://idp.acme.com/oauth2/v1/logout"
        name="endSessionUri"
        id={`${SSO_FIELD_ID}endSessionUri`}
        sx={invalidFieldFocusSx}
        value={endSessionUri}
        onChange={(e: any) => setEndSessionUri(e.target.value)}
        error={!!oidcFieldErrors.endSessionUri}
        helperText={
          oidcFieldErrors.endSessionUri ||
          "If your IdP supports RP-initiated logout and you want signing out of this app to also sign the user out of your IdP, paste its end session endpoint here."
        }
      />

      <CustomInput
        required
        label="Client ID"
        placeHolder="e.g. 0oa1b2c3d4e5f6g7h8i9"
        name="clientId"
        id={`${SSO_FIELD_ID}clientId`}
        sx={invalidFieldFocusSx}
        value={clientId}
        onChange={(e: any) => setClientId(e.target.value)}
        error={!!oidcFieldErrors.clientId}
        helperText={oidcFieldErrors.clientId}
      />
      <CustomInput
        required={!oidcHasStoredSecret}
        label="Client Secret"
        placeHolder={
          oidcHasStoredSecret
            ? "Leave blank to keep the stored value"
            : "Paste the client secret from your IdP"
        }
        name="clientSecret"
        id={`${SSO_FIELD_ID}clientSecret`}
        sx={invalidFieldFocusSx}
        value={clientSecret}
        onChange={(e: any) => setClientSecret(e.target.value)}
        error={!!oidcFieldErrors.clientSecret}
        helperText={oidcFieldErrors.clientSecret}
      />

      <FormControl
        fullWidth
        size="small"
        error={!!oidcFieldErrors.clientAuthenticationMethod}
      >
        <InputLabel id="oidc-client-auth-method-label" required>
          Client Authentication Method
        </InputLabel>
        <Select
          labelId="oidc-client-auth-method-label"
          SelectDisplayProps={{
            id: `${SSO_FIELD_ID}clientAuthenticationMethod`,
            "aria-invalid":
              Boolean(oidcFieldErrors.clientAuthenticationMethod) || undefined,
            "aria-describedby": `${SSO_FIELD_ID}clientAuthenticationMethod_helper`,
          }}
          sx={invalidFieldFocusSx}
          name="clientAuthenticationMethod"
          label="Client Authentication Method"
          value={clientAuthenticationMethod}
          onChange={(e) => {
            setClientAuthenticationMethod(
              e.target.value as "client_secret_basic" | "client_secret_post",
            );
            setOidcFieldErrors((prev) => ({
              ...prev,
              clientAuthenticationMethod: undefined,
            }));
          }}
        >
          {CLIENT_AUTH_METHODS.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText
          id={`${SSO_FIELD_ID}clientAuthenticationMethod_helper`}
        >
          {oidcFieldErrors.clientAuthenticationMethod ||
            "How this app authenticates itself when exchanging the authorization code."}
        </FormHelperText>
      </FormControl>

      <FormControlLabel
        control={
          <CustomSwitch
            checked={usePkce}
            onChange={(e: any) => setUsePkce(e.target.checked)}
            name="usePkce"
          />
        }
        label={
          <Typography sx={{ fontSize: "13px", color: "#475569", ml: 1 }}>
            Use PKCE (recommended)
          </Typography>
        }
        sx={{ ml: 0 }}
      />

      {renderScopesField()}
    </>
  );

  const renderAdvanced = () => (
    <Box>
      <Button
        variant="text"
        size="small"
        onClick={() => setShowAdvanced((s) => !s)}
        endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: "none", color: "#64748B", px: 0 }}
      >
        {protocol === "SAML"
          ? "Advanced (SP keypair & attribute mapping)"
          : "Advanced (claim mapping & auth request options)"}
      </Button>
      <Collapse in={showAdvanced}>
        <Stack spacing={2} mt={1}>
          {protocol === "SAML" ? (
            <>
              <CustomInput
                label="Email Attribute"
                placeHolder="http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
                name="emailClaim"
                value={emailClaim}
                onChange={(e: any) => setEmailClaim(e.target.value)}
              />
              <CustomInput
                label="SP Entity ID (override)"
                placeHolder="Optional"
                name="spEntityId"
                value={spEntityId}
                onChange={(e: any) => setSpEntityId(e.target.value)}
              />
              <CustomInput
                multiline
                minRows={3}
                maxRows={8}
                label="SP Public Certificate (PEM)"
                placeHolder="Optional — for signed AuthnRequests / encrypted assertions"
                name="spCertificate"
                value={spCertificate}
                onChange={(e: any) => setSpCertificate(e.target.value)}
              />
              <CustomInput
                label="SP Private Key Reference (Secrets Manager)"
                placeHolder={
                  isEditMode
                    ? "Leave blank to keep the stored value"
                    : "e.g. SS_SAML_SP_KEY_inkfree-acme"
                }
                name="spPrivateKeyRef"
                value={spPrivateKeyRef}
                onChange={(e: any) => setSpPrivateKeyRef(e.target.value)}
              />
              <CustomInput
                multiline
                minRows={3}
                maxRows={8}
                label="SP Private Key (PEM, discouraged)"
                placeHolder={
                  isEditMode
                    ? "Leave blank to keep the stored value"
                    : "Paste the SP private key, PEM format"
                }
                name="spPrivateKey"
                value={spPrivateKey}
                onChange={(e: any) => setSpPrivateKey(e.target.value)}
              />
            </>
          ) : (
            <>
              <CustomInput
                label="Email Claim (override)"
                placeHolder="email"
                name="emailClaim"
                value={emailClaim}
                onChange={(e: any) => setEmailClaim(e.target.value)}
              />
              <CustomInput
                label="ACR Values (optional)"
                placeHolder="Space-separated, e.g. urn:mace:incommon:iap:silver"
                name="acrValues"
                value={acrValuesInput}
                onChange={(e: any) => setAcrValuesInput(e.target.value)}
                helperText="Requested Authentication Context Class References, space-separated."
              />
              <FormControl fullWidth size="small">
                <InputLabel id="oidc-prompt-label">Prompt (optional)</InputLabel>
                <Select
                  labelId="oidc-prompt-label"
                  id="oidc-prompt"
                  name="prompt"
                  label="Prompt (optional)"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Default (let the IdP decide)</em>
                  </MenuItem>
                  {PROMPT_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Controls whether the IdP re-prompts for login/consent.
                </FormHelperText>
              </FormControl>
              <CustomInput
                type="number"
                label="Max Age (seconds, optional)"
                placeHolder="e.g. 3600"
                name="maxAge"
                value={maxAge}
                onChange={(e: any) => {
                  const v = e.target.value;
                  setMaxAge(v === "" ? "" : Number(v));
                }}
                helperText="Force re-authentication if the user's last IdP login is older than this."
              />
            </>
          )}
        </Stack>
      </Collapse>
    </Box>
  );

  const renderSpInfo = () => {
    if (!config) return null;
    const sp = config.saml?.sp;
    const acsUrl = sp?.acsUrl ?? `${urls.SAML_SP_ACS}/${config.registrationId}`;
    const metadataUrlValue =
      sp?.entityId ??
      sp?.metadataUrl ??
      `${urls.SAML_SP_METADATA}/${config.registrationId}`;
    const sloUrl = sp?.sloUrl ?? urls.SAML_SP_SLO;
    return (
      <Box
        sx={{
          border: "1px solid #BFDBFE",
          borderRadius: "10px",
          p: 1.5,
          bgcolor: "#EFF6FF",
        }}
      >
        <Typography
          sx={{ fontSize: "13px", fontWeight: 700, color: "#1D4ED8", mb: 1 }}
        >
          Configure these values in your IdP
        </Typography>
        <Stack spacing={1}>
          <CopyableUrl
            label="ACS URL / Reply URL / Single Sign-On URL"
            value={acsUrl}
          />
          <CopyableUrl
            label="SP Entity ID / Audience URI / Metadata URL (import instead of typing manually)"
            value={metadataUrlValue}
          />
          <CopyableUrl
            label="SP Single Logout URL (only needed if entered manually in your IdP)"
            value={sloUrl}
          />
        </Stack>

        <Box sx={{ borderTop: "1px solid #BFDBFE", mt: 1.5, pt: 1.5 }}>
          <Typography
            sx={{ fontSize: "11px", fontWeight: 600, color: "#1D4ED8", mb: 0.5 }}
          >
            SP Signing &amp; Subject Settings
          </Typography>
          <Stack spacing={1}>
            <CopyableUrl
              label="Signature / Digest Algorithm"
              value="RSA-SHA256"
            />
            <CopyableUrl
              label="NameID Format (subject identifier = user's email)"
              value="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress"
            />
          </Stack>
        </Box>

        <Box sx={{ borderTop: "1px solid #BFDBFE", mt: 1.5, pt: 1.5 }}>
          <Typography
            sx={{ fontSize: "11px", fontWeight: 600, color: "#1D4ED8", mb: 0.25 }}
          >
            SP Signing Certificate
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "#475569", mb: 1 }}>
            Some IdPs require the SP's public certificate to enable request
            signing or Single Logout. Download it and upload to your IdP.
          </Typography>
          <Stack direction="row" spacing={1}>
            {(["crt", "pem"] as const).map((format) => (
              <Button
                key={format}
                variant="outlined"
                size="small"
                disabled={!!certDownloading}
                onClick={() => handleDownloadCertificate(format)}
                startIcon={
                  certDownloading === format ? (
                    <CircularProgress size={14} thickness={5} />
                  ) : (
                    <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  fontSize: "12px",
                  color: "#1D4ED8",
                  borderColor: "#BFDBFE",
                }}
              >
                Download .{format}
              </Button>
            ))}
          </Stack>
        </Box>
      </Box>
    );
  };

  const renderRpInfo = () => {
    if (!config) return null;
    const rp = config.oidc?.rp;
    // Prefer the backend's own values -- it's the source of truth for what domain this SP is
    // actually reachable on (per-tenant custom domain, local dev against a tunneled backend, etc.).
    // The urls.* fallbacks only apply if an older backend response doesn't carry these fields yet.
    const redirectUri =
      rp?.redirectUri ?? `${urls.OIDC_REDIRECT_URI_BASE}/${config.registrationId}`;
    const postLogoutRedirectUri =
      rp?.postLogoutRedirectUri ?? urls.OIDC_POST_LOGOUT_REDIRECT_URI;
    const initiateLoginUri =
      rp?.initiateUri ?? `${urls.OIDC_INITIATE_URI_BASE}/${config.registrationId}`;
    const sharedInitiateLoginUri = rp?.sharedInitiateUri ?? urls.OIDC_INITIATE_URI_BASE;
    return (
      <Box
        sx={{
          border: "1px solid #BFDBFE",
          borderRadius: "10px",
          p: 1.5,
          bgcolor: "#EFF6FF",
        }}
      >
        <Typography
          sx={{ fontSize: "13px", fontWeight: 700, color: "#1D4ED8", mb: 1 }}
        >
          Configure these values in your IdP
        </Typography>
        <Stack spacing={1}>
          <CopyableUrl label="Redirect URI / Callback URL" value={redirectUri} />
          <CopyableUrl
            label="Post-Logout Redirect URI"
            value={postLogoutRedirectUri}
          />
          <CopyableUrl
            label="Initiate Login URI (for IdP-initiated / app-tile login)"
            value={initiateLoginUri}
          />
        </Stack>
        <Typography
          variant="caption"
          sx={{ display: "block", color: "#475569", mt: 1.25 }}
        >
          If your IdP requires one shared Initiate Login URI, use{" "}
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {sharedInitiateLoginUri}
          </Box>{" "}
          instead. It works when this issuer is not shared with another connector
          on the account.
        </Typography>
      </Box>
    );
  };

  const getReserveStep = () => (
    <Stack spacing={2.5} sx={{ textAlign: "left" }}>
      {renderProtocolToggle()}
      <CustomInput
        label="Connector name"
        placeHolder={`Optional — defaults to ${props.defaultSlug ?? "app-account"}`}
        name="slug"
        id={`${SSO_FIELD_ID}slug`}
        sx={invalidFieldFocusSx}
        value={slug}
        onChange={(e: any) => {
          setSlug(e.target.value);
          setSlugError("");
        }}
        error={!!slugError}
        helperText={
          slugError ||
          (slug.trim()
            ? `Saved as "${normalizeSlug(slug)}"`
            : "A friendly label, e.g. acme-okta")
        }
        propsToInputElement={{ maxLength: MAX_SLUG_LENGTH }}
      />
      {renderDomainsField()}
      {renderPolicySwitches()}
      {renderProvisioning()}
    </Stack>
  );

  const getSpInfoStep = () => (
    <Stack spacing={2} sx={{ textAlign: "left" }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckCircleOutlineIcon sx={{ color: "#047857", fontSize: 20 }} />
        <Typography sx={{ fontSize: "13px", color: "#047857", fontWeight: 600 }}>
          Connector reserved. It won't accept logins until you finish setup.
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {protocol === "SAML"
          ? "Paste these Service Provider URLs into your identity provider (Okta, Entra ID, Google Workspace, OneLogin, Ping…). Your IdP will then give you the details to enter on the next step."
          : "Use all of these RP URLs when creating your OIDC / OAuth 2.0 app in your identity provider (Okta, Entra ID, Google Workspace, Auth0, Ping…). After the app is created, enter the issuer, client ID, and client secret in the next step."}
      </Typography>
      {protocol === "SAML" ? renderSpInfo() : renderRpInfo()}
    </Stack>
  );

  const getIdpStep = () => (
    <Stack spacing={2.5} sx={{ textAlign: "left" }}>
      <Typography variant="body2" color="text.secondary">
        {protocol === "SAML"
          ? "Enter the details your IdP produced. Choose a metadata URL for the simplest setup, or enter the fields manually."
          : "Enter the details your IdP produced. Choose an issuer URL for the simplest setup (we'll discover the rest), or enter the endpoints manually."}
      </Typography>
      {protocol === "SAML" ? renderIdpFields() : renderOidcFields()}
      {renderAdvanced()}
    </Stack>
  );

  const getEditBody = () => (
    <Stack spacing={2.5} sx={{ textAlign: "left" }}>
      {config?.status === "Draft" && (
        <Typography
          sx={{
            fontSize: "12px",
            color: "#B45309",
            bgcolor: "#FEF3C7",
            border: "1px solid #FDE68A",
            borderRadius: "8px",
            px: 1.5,
            py: 1,
          }}
        >
          Setup is incomplete. Add the IdP details below to activate this
          connector.
        </Typography>
      )}
      {protocol === "SAML" ? renderSpInfo() : renderRpInfo()}
      <CustomInput
        label="Connector name"
        name="slug"
        value={slug}
        disabled
        helperText="Connector name can't be changed"
      />
      <CustomInput
        label="Protocol"
        name="protocol"
        value={PROTOCOL_LABEL[protocol]}
        disabled
        helperText="Protocol can't be changed after the connector is created"
      />
      {renderDomainsField()}
      {protocol === "SAML" ? renderIdpFields() : renderOidcFields()}
      {renderPolicySwitches()}
      {renderProvisioning()}
      {renderAdvanced()}
    </Stack>
  );

  const getBody = () => {
    if (isEditMode) return getEditBody();
    if (step === 0) return getReserveStep();
    if (step === 1) return getSpInfoStep();
    return getIdpStep();
  };

  const submitButton = (label: string, busyLabel: string, onClick: () => void) => (
    <Button
      variant="contained"
      onClick={onClick}
      disabled={isLoading}
      startIcon={
        isLoading ? (
          <CircularProgress size={15} thickness={5} color="inherit" />
        ) : undefined
      }
    >
      {isLoading ? busyLabel : label}
    </Button>
  );

  const cancelButton = (label = "Cancel") => (
    <Button
      variant="outlined"
      onClick={() => props.handleDialogClose()}
      disabled={isLoading}
    >
      {label}
    </Button>
  );

  const getFooter = () => {
    if (isEditMode) {
      return (
        <>
          {cancelButton()}
          {submitButton(
            config?.status === "Draft" ? "Complete setup" : "Save Changes",
            "Saving...",
            saveEditHandler,
          )}
        </>
      );
    }
    if (step === 0) {
      return (
        <>
          {cancelButton()}
          {submitButton("Reserve connector", "Reserving...", reserveHandler)}
        </>
      );
    }
    if (step === 1) {
      return (
        <>
          {cancelButton("Close")}
          <Button variant="contained" onClick={() => setStep(2)}>
            Continue to IdP details →
          </Button>
        </>
      );
    }
    return (
      <>
        <Button
          variant="outlined"
          onClick={() => setStep(1)}
          disabled={isLoading}
        >
          Back
        </Button>
        {submitButton("Activate SSO", "Activating...", activateHandler)}
      </>
    );
  };

  const heading = isEditMode
    ? config?.status === "Draft"
      ? "Complete SSO Setup"
      : "Edit SSO Configuration"
    : "Add SSO Configuration";
  const subheading = isEditMode
    ? `Update your enterprise identity provider (${PROTOCOL_LABEL[protocol]})`
    : `Set up your enterprise identity provider (${PROTOCOL_LABEL[protocol]})`;

  return (
    <>
      <CustomLoader isLoading={isLoading} />
      <AppModal
        open={props.isVisible}
        onClose={() => props.handleDialogClose()}
        title={heading}
        subtitle={subheading}
        // A multi-field connector form — the design system's L width.
        size="lg"
        busy={isLoading}
        footer={getFooter()}
      >
        {/* The step rail belongs to the body: the shell's header slot is title
            and subtitle only. */}
        {!isEditMode && (
          <Box sx={{ mb: 2, width: "100%" }}>
            <Stepper activeStep={step} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}
        <Stack>{getBody()}</Stack>
      </AppModal>
      {confirmActivation && (
        <AppModal
          open={!!confirmActivation}
          onClose={() => setConfirmActivation(null)}
          title="Please confirm"
          // Continuing takes the sibling connection offline.
          tone="destructive"
          size="sm"
          busy={isLoading}
          primaryLabel="Continue"
          onPrimary={() => confirmActivation.run()}
          primaryLoading={isLoading}
          cancelLabel="Cancel"
          onCancel={() => setConfirmActivation(null)}
        >
          <Typography sx={bodyTextSx}>
            {`Activating ${PROTOCOL_LABEL[protocol]} will deactivate your current ${
              PROTOCOL_LABEL[confirmActivation.sibling.protocol ?? "SAML"]
            } connection (`}
            <span style={{ fontWeight: 700 }}>
              {confirmActivation.sibling.slug ||
                confirmActivation.sibling.registrationId}
            </span>
            {"). Continue?"}
          </Typography>
        </AppModal>
      )}
    </>
  );
};

export default AddSsoConnectorModal;
